import { z } from 'zod';
import { CountryCodeSchema } from './geo';
import { PhoneSchema } from './auth';
import { MenuFoodTypeSchema } from './menu';
import { EtaRangeSchema } from './discovery';
import { OrderStatusSchema } from './orders';
import {
  COD_TIERS,
  OrderPaymentViewSchema,
  PaymentMethodSchema,
  PaymentStatusSchema,
} from './payments';

/* ------------------------------------------------------------------ */
/* Cart, bill, checkout (W10)                                          */
/*                                                                     */
/* The cart lives on the phone; the server prices it every time        */
/* (quote) and again when the order is placed, so the app never        */
/* decides a price. One restaurant per cart. Payments are W11: until   */
/* then an order is Cash on Delivery, or a development-only "test      */
/* payment" that marks it prepaid.                                     */
/* ------------------------------------------------------------------ */

/* ---------------- cart ---------------- */

export const MAX_CART_LINES = 30;
export const MAX_LINE_QUANTITY = 20;

export const CartComboPickSchema = z.object({
  slotId: z.uuid(),
  itemIds: z.array(z.uuid()).min(1).max(10),
});
export type CartComboPick = z.infer<typeof CartComboPickSchema>;

export const CartLineInputSchema = z.object({
  itemId: z.uuid(),
  variantId: z.uuid().nullable().default(null),
  addonOptionIds: z.array(z.uuid()).max(20).default([]),
  comboPicks: z.array(CartComboPickSchema).max(10).default([]),
  quantity: z.number().int().min(1).max(MAX_LINE_QUANTITY),
  /** Cooking note for this dish ("less spicy"). */
  note: z
    .string()
    .trim()
    .max(120)
    .nullable()
    .default(null)
    .transform((value) => (value === '' ? null : value)),
});
export type CartLineInput = z.infer<typeof CartLineInputSchema>;

export const CartInputSchema = z.object({
  outletId: z.uuid(),
  lines: z.array(CartLineInputSchema).min(1, 'Your cart is empty').max(MAX_CART_LINES),
});
export type CartInput = z.infer<typeof CartInputSchema>;

/* ---------------- quote ---------------- */

const couponCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{3,20}$/, 'Coupon codes are 3–20 letters and numbers');

const isoInstant = z.iso.datetime({ offset: true });

export const QuoteInputSchema = z.object({
  cart: CartInputSchema,
  /** Where the food goes. Signed-in customers send their saved address too. */
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  addressId: z.uuid().nullable().default(null),
  couponCode: couponCode.nullable().default(null),
  tipMinor: z.number().int().min(0).default(0),
  /** Deliver later; null = now. */
  scheduledFor: isoInstant.nullable().default(null),
  paymentMethod: PaymentMethodSchema.nullable().default(null),
  /** Use Capsico Money first (on by default when there is a balance). */
  useWallet: z.boolean().default(true),
});
export type QuoteInput = z.infer<typeof QuoteInputSchema>;

export const OFFER_FUNDERS = ['restaurant', 'capsico', 'shared'] as const;
export const OfferFunderSchema = z.enum(OFFER_FUNDERS);
export type OfferFunder = z.infer<typeof OfferFunderSchema>;
export const OFFER_FUNDER_LABELS: Record<OfferFunder, string> = {
  restaurant: 'Offer by Restaurant',
  capsico: 'Offer by Capsico',
  shared: 'Offer by Restaurant + Capsico',
};

export const BILL_LINE_KEYS = [
  'items',
  'packaging',
  'delivery',
  'platform',
  'small_order',
  'surcharge',
  'fee_cap',
  'offer',
  'food_tax',
  'tip',
  'rounding',
] as const;
export const BillLineKeySchema = z.enum(BILL_LINE_KEYS);
export type BillLineKey = z.infer<typeof BillLineKeySchema>;

export const BillLineSchema = z.object({
  key: BillLineKeySchema,
  label: z.string(),
  /** Signed: discounts and the fee cap are negative. */
  amountMinor: z.number().int(),
  /** The full price when it was waived or reduced (shown struck through). */
  struckMinor: z.number().int().nullable(),
  /** Short line under the label ("Free for your first 3 orders"). */
  note: z.string().nullable(),
  /** "Why this fee" sheet text. */
  why: z.string().nullable(),
  funding: OfferFunderSchema.nullable(),
});
export type BillLine = z.infer<typeof BillLineSchema>;

export const BillSchema = z.object({
  country: CountryCodeSchema,
  lines: z.array(BillLineSchema),
  /** What the customer pays. */
  totalMinor: z.number().int(),
  /** Everything taken off: offers, waived fees, the fee cap. */
  savingsMinor: z.number().int(),
});
export type Bill = z.infer<typeof BillSchema>;

export const QuoteLineSchema = z.object({
  /** Position in the cart the phone sent. */
  index: z.number().int(),
  itemId: z.uuid(),
  name: z.string(),
  foodType: MenuFoodTypeSchema,
  variantName: z.string().nullable(),
  addons: z.array(z.object({ name: z.string(), priceMinor: z.number().int() })),
  comboChoices: z.array(z.string()),
  quantity: z.number().int(),
  unitPriceMinor: z.number().int(),
  lineTotalMinor: z.number().int(),
  note: z.string().nullable(),
  /** Why this line cannot be ordered now (sold out, choices changed); null when fine. */
  issue: z.string().nullable(),
});
export type QuoteLine = z.infer<typeof QuoteLineSchema>;

export const OfferViewSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  kind: z.enum(['ladder', 'coupon']),
  fundedBy: OfferFunderSchema,
  /** What it takes off this cart, when eligible. */
  discountMinor: z.number().int(),
  eligible: z.boolean(),
  /** Plain words when not eligible ("Add ₹40 more", "For your first order"). */
  reason: z.string().nullable(),
  applied: z.boolean(),
});
export type OfferView = z.infer<typeof OfferViewSchema>;

export const PaymentOptionSchema = z.object({
  method: PaymentMethodSchema,
  available: z.boolean(),
  reason: z.string().nullable(),
  /** Short line under the method ("Pay with any UPI app", "Scan with your bank app"). */
  note: z.string().nullable().default(null),
});
export type PaymentOption = z.infer<typeof PaymentOptionSchema>;

export const QuoteSchema = z.object({
  outlet: z.object({
    id: z.uuid(),
    name: z.string(),
    locality: z.string(),
    country: CountryCodeSchema,
    open: z.boolean(),
    deliverable: z.boolean(),
    /** Why it cannot take this order now. */
    reason: z.string().nullable(),
  }),
  lines: z.array(QuoteLineSchema),
  itemCount: z.number().int(),
  bill: BillSchema,
  minimumOrder: z.object({ minor: z.number().int(), shortByMinor: z.number().int() }),
  distanceKm: z.number(),
  eta: EtaRangeSchema.nullable(),
  offers: z.array(OfferViewSchema),
  appliedCoupon: z.string().nullable(),
  /** The code the customer typed did not work, and why. */
  couponError: z.string().nullable(),
  freeDelivery: z.object({ applied: z.boolean(), ordersLeft: z.number().int() }),
  payment: z.object({
    methods: z.array(PaymentOptionSchema),
    codTier: z.enum(COD_TIERS).nullable(),
    /** Capsico Money: what the customer has, and what this order would use. */
    wallet: z.object({
      balanceMinor: z.number().int(),
      applyMinor: z.number().int(),
      /** The soonest-expiring credit, so the app can say "₹50 expires on 12 Nov". */
      nextExpiry: z.object({ amountMinor: z.number().int(), at: z.string() }).nullable(),
    }),
    /** Left to pay after Capsico Money (cash at the door or online). */
    payableMinor: z.number().int(),
  }),
  schedule: z.object({
    /** False when the kitchen or town is closed now; scheduling may still work. */
    canOrderNow: z.boolean(),
    /** Delivery slots the customer can pick (ISO instants, 30 minutes apart). */
    slots: z.array(z.string()),
    prepaidOnly: z.boolean(),
  }),
  tip: z.object({ presetsMinor: z.array(z.number().int()), maxMinor: z.number().int() }),
  /** Reasons the order cannot be placed yet; empty when it can. */
  blockers: z.array(z.string()),
  /** Signed-in customers only (guests can look, not order). */
  signedIn: z.boolean(),
});
export type Quote = z.infer<typeof QuoteSchema>;

/* ---------------- place ---------------- */

export const PlaceOrderInputSchema = QuoteInputSchema.extend({
  addressId: z.uuid('Pick a delivery address'),
  paymentMethod: PaymentMethodSchema,
  /** Note for the kitchen about the whole order. */
  kitchenNote: z
    .string()
    .trim()
    .max(300)
    .nullable()
    .default(null)
    .transform((value) => (value === '' ? null : value)),
  /** Send spoons and tissues (off by default). */
  cutlery: z.boolean().default(false),
  /** Ordering for someone else: overrides the address's receiver for this order. */
  receiverName: z
    .string()
    .trim()
    .max(60)
    .nullable()
    .default(null)
    .transform((value) => (value === '' ? null : value)),
  receiverPhone: PhoneSchema.nullable().default(null),
  /** The total the customer agreed to. If prices moved since, the order is refused with the new quote. */
  expectedTotalMinor: z.number().int().min(0),
  /** W14: "Leave at the door" — no delivery code, the rider takes a photo instead. */
  leaveAtDoor: z.boolean().optional(),
});
export type PlaceOrderInput = z.infer<typeof PlaceOrderInputSchema>;

/* ---------------- customer orders ---------------- */

export const CUSTOMER_ORDER_STAGES = [
  'awaiting_payment',
  'scheduled',
  'placed',
  'preparing',
  'ready',
  'on_the_way',
  'delivered',
  'cancelled',
] as const;
export const CustomerOrderStageSchema = z.enum(CUSTOMER_ORDER_STAGES);
export type CustomerOrderStage = z.infer<typeof CustomerOrderStageSchema>;

export const CustomerOrderRowSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  outlet: z.object({ id: z.uuid(), name: z.string(), locality: z.string() }),
  country: CountryCodeSchema,
  status: OrderStatusSchema,
  stage: CustomerOrderStageSchema,
  itemsSummary: z.string(),
  itemCount: z.number().int(),
  totalMinor: z.number().int(),
  paymentMethod: PaymentMethodSchema,
  paymentStatus: PaymentStatusSchema,
  /** Capsico Money used on this order. */
  walletMinor: z.number().int(),
  /** Money given back so far (Capsico Money + original method). */
  refundedMinor: z.number().int(),
  placedAt: z.string(),
  scheduledFor: z.string().nullable(),
  cancelledReason: z.string().nullable(),
});
export type CustomerOrderRow = z.infer<typeof CustomerOrderRowSchema>;

export const CustomerOrderDetailSchema = CustomerOrderRowSchema.extend({
  lines: z.array(
    z.object({
      name: z.string(),
      foodType: MenuFoodTypeSchema,
      variantName: z.string().nullable(),
      addons: z.array(z.object({ name: z.string(), priceMinor: z.number().int() })),
      quantity: z.number().int(),
      lineTotalMinor: z.number().int(),
      note: z.string().nullable(),
    }),
  ),
  bill: BillSchema,
  address: z.object({
    label: z.string(),
    line: z.string(),
    receiverName: z.string().nullable(),
  }),
  kitchenNote: z.string().nullable(),
  cutlery: z.boolean(),
  eta: EtaRangeSchema.nullable(),
  offerCode: z.string().nullable(),
  /** Payment progress, refunds and tax invoices for this order (W11). */
  payment: OrderPaymentViewSchema,
});
export type CustomerOrderDetail = z.infer<typeof CustomerOrderDetailSchema>;

export const CustomerOrderListSchema = z.object({ rows: z.array(CustomerOrderRowSchema) });
export type CustomerOrderList = z.infer<typeof CustomerOrderListSchema>;

/* ---------------- offers (Capsico OS) ---------------- */

export const OFFER_KINDS = ['ladder', 'coupon'] as const;
export const OfferKindSchema = z.enum(OFFER_KINDS);
export type OfferKind = z.infer<typeof OfferKindSchema>;
export const OFFER_KIND_LABELS: Record<OfferKind, string> = {
  ladder: 'Welcome ladder',
  coupon: 'Coupon',
};

/**
 * all: listed to everyone who matches. granted: listed only to customers the growth engine granted
 * it to (referral friend offer, creator codes, win-back COMEBACK30) — W18.
 */
export const OFFER_AUDIENCES = ['all', 'granted'] as const;
export const OfferAudienceSchema = z.enum(OFFER_AUDIENCES);
export type OfferAudience = z.infer<typeof OfferAudienceSchema>;
export const OFFER_AUDIENCE_LABELS: Record<OfferAudience, string> = {
  all: 'Everyone',
  granted: 'Only people it was given to',
};

export const OFFER_STATUSES = ['active', 'paused', 'ended'] as const;
export const OfferStatusSchema = z.enum(OFFER_STATUSES);
export type OfferStatus = z.infer<typeof OfferStatusSchema>;

export const DISCOUNT_TYPES = ['flat', 'percent'] as const;
export const DiscountTypeSchema = z.enum(DISCOUNT_TYPES);
export type DiscountType = z.infer<typeof DiscountTypeSchema>;

export const OfferRowSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  kind: OfferKindSchema,
  /** Welcome ladder: 1 = first order, 2 = second, 3 = third. */
  ladderStep: z.number().int().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  country: CountryCodeSchema,
  townId: z.uuid().nullable(),
  townName: z.string().nullable(),
  outletId: z.uuid().nullable(),
  outletName: z.string().nullable(),
  discountType: DiscountTypeSchema,
  flatMinor: z.number().int().nullable(),
  percent: z.number().int().nullable(),
  maxDiscountMinor: z.number().int().nullable(),
  minOrderMinor: z.number().int(),
  funding: OfferFunderSchema,
  /** Shared offers: Capsico's share of each discount. */
  capsicoSharePercent: z.number().int(),
  perCustomerLimit: z.number().int(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  status: OfferStatusSchema,
  /** W18: granted offers only show to people the growth engine gave them to. */
  audience: OfferAudienceSchema,
  usedCount: z.number().int(),
  discountGivenMinor: z.number().int(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type OfferRow = z.infer<typeof OfferRowSchema>;

export const OfferListQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  status: OfferStatusSchema.optional(),
  kind: OfferKindSchema.optional(),
  search: z.string().trim().max(40).optional(),
});
export type OfferListQuery = z.infer<typeof OfferListQuerySchema>;

export const OfferListSchema = z.object({
  rows: z.array(OfferRowSchema),
  counts: z.record(OfferStatusSchema, z.number().int()),
});
export type OfferList = z.infer<typeof OfferListSchema>;

const OfferFieldsSchema = z.object({
  code: couponCode,
  kind: OfferKindSchema,
  ladderStep: z.number().int().min(1).max(10).nullable().default(null),
  title: z.string().trim().min(3, 'Add a short title').max(60),
  description: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .default(null)
    .transform((value) => (value === '' ? null : value)),
  country: CountryCodeSchema,
  townId: z.uuid().nullable().default(null),
  outletId: z.uuid().nullable().default(null),
  discountType: DiscountTypeSchema,
  flatMinor: z.number().int().min(1).nullable().default(null),
  percent: z.number().int().min(1).max(100).nullable().default(null),
  maxDiscountMinor: z.number().int().min(1).nullable().default(null),
  minOrderMinor: z.number().int().min(0).default(0),
  funding: OfferFunderSchema,
  capsicoSharePercent: z.number().int().min(0).max(100).default(0),
  perCustomerLimit: z.number().int().min(1).max(100).default(1),
  startsAt: isoInstant.nullable().default(null),
  endsAt: isoInstant.nullable().default(null),
});

function checkOffer(value: z.infer<typeof OfferFieldsSchema>, ctx: z.RefinementCtx): void {
  if (value.discountType === 'flat' && value.flatMinor === null) {
    ctx.addIssue({ code: 'custom', path: ['flatMinor'], message: 'Set the amount off' });
  }
  if (value.discountType === 'percent' && value.percent === null) {
    ctx.addIssue({ code: 'custom', path: ['percent'], message: 'Set the percentage off' });
  }
  if (value.discountType === 'percent' && value.maxDiscountMinor === null) {
    ctx.addIssue({
      code: 'custom',
      path: ['maxDiscountMinor'],
      message: 'Percentage offers need a highest discount',
    });
  }
  if (value.kind === 'ladder' && value.ladderStep === null) {
    ctx.addIssue({ code: 'custom', path: ['ladderStep'], message: 'Pick which order it is for' });
  }
  if (value.kind === 'coupon' && value.ladderStep !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['ladderStep'],
      message: 'Only welcome-ladder offers have a step',
    });
  }
  if (
    value.funding === 'shared' &&
    (value.capsicoSharePercent < 1 || value.capsicoSharePercent > 99)
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['capsicoSharePercent'],
      message: "Set Capsico's share between 1 and 99%",
    });
  }
  if (
    value.funding !== 'shared' &&
    value.capsicoSharePercent !== 0 &&
    value.capsicoSharePercent !== 100
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['capsicoSharePercent'],
      message: 'Only shared offers split the cost',
    });
  }
  if (value.funding === 'restaurant' && value.kind === 'coupon' && value.outletId === null) {
    ctx.addIssue({
      code: 'custom',
      path: ['outletId'],
      message: 'A restaurant-funded coupon needs its outlet',
    });
  }
  if (value.startsAt && value.endsAt && value.startsAt >= value.endsAt) {
    ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'End after it starts' });
  }
}

export const OfferInputSchema = OfferFieldsSchema.superRefine(checkOffer);
export type OfferInput = z.infer<typeof OfferInputSchema>;

export const UpdateOfferInputSchema = OfferFieldsSchema.extend({
  expectedVersion: z.number().int().min(1),
}).superRefine(checkOffer);
export type UpdateOfferInput = z.infer<typeof UpdateOfferInputSchema>;

export const OfferStatusInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  status: OfferStatusSchema,
});
export type OfferStatusInput = z.infer<typeof OfferStatusInputSchema>;

/** Capsico's money share of one discount (shared offers split it by percent). */
export function capsicoShareMinor(
  discountMinor: number,
  funding: OfferFunder,
  capsicoSharePercent: number,
): number {
  if (funding === 'capsico') return discountMinor;
  if (funding === 'restaurant') return 0;
  return Math.round((discountMinor * capsicoSharePercent) / 100);
}
